import { IFacility, IFacilityServiceability, IPlatformServiceability } from '@admin-panel/types';
import { useCustomMutation } from '@refinedev/core';
import { useState, useEffect, useMemo, useCallback } from 'react';

export function usePlatformServiceabilityData(skuCodes: string[], pincode?: string) {
  const [serviceability, setServiceability] = useState<IPlatformServiceability | undefined>();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  const { mutate: serviceabilityApiMutation } = useCustomMutation<IPlatformServiceability>();

  // Use Refine's useCustom hook for data fetching
  const fetchServiceabilityData = useCallback(async () => {
    setIsLoading(true);
    serviceabilityApiMutation(
      {
        url: '',
        method: 'post',
        meta: {
          resource: 'serviceability',
          action: 'fetchPlatformServiceability',
        },
        values: {
          skuCodes,
          destinationPincode: pincode,
        },
      },
      {
        onError: (error, variables, context) => {
          // An error occurred!
          setIsLoading(false);
          setError(error.message);
        },
        onSuccess: (data, variables, context) => {
          setIsLoading(false);
          setServiceability(data.data);
        },
      },
    );
  }, [skuCodes, pincode]);

  const enrichmentKey = useMemo(() => {
    return JSON.stringify({ skuCodes, pincode: pincode || '' });
  }, [skuCodes, pincode]);

  useEffect(() => {
    if (pincode && skuCodes && skuCodes.length > 0) {
      fetchServiceabilityData();
    } else {
      setIsLoading(false);
      setServiceability(undefined);
    }
  }, [enrichmentKey]);

  return {
    serviceability,
    isLoading,
    error,
  };
}

export function useServiceabilityData(
  skuCodes: string[],
  sourceFacility?: IFacility,
  pincode?: string,
) {
  const [serviceability, setServiceability] = useState<IFacilityServiceability[] | undefined>();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  const { mutate: serviceabilityApiMutation } = useCustomMutation<IFacilityServiceability[]>();

  // Use Refine's useCustom hook for data fetching
  const fetchServiceabilityData = useCallback(async () => {
    setIsLoading(true);
    serviceabilityApiMutation(
      {
        url: '',
        method: 'post',
        meta: {
          resource: 'serviceability',
          action: 'fetchFacilityServiceability',
        },
        values: {
          skuCodes,
          destinationPincodes: [pincode],
          sourceFacilities: [sourceFacility],
        },
      },
      {
        onError: (error, variables, context) => {
          // An error occurred!
          setIsLoading(false);
          setError(error.message);
        },
        onSuccess: (data, variables, context) => {
          setIsLoading(false);
          setServiceability(data.data);
        },
      },
    );
  }, [skuCodes, pincode, sourceFacility]);

  const enrichmentKey = useMemo(() => {
    return JSON.stringify({
      skuCodes,
      pincode: pincode || '',
      sourceFacilityId: sourceFacility?.id || 0,
    });
  }, [skuCodes, pincode, sourceFacility]);

  useEffect(() => {
    if (pincode && skuCodes && skuCodes.length > 0 && sourceFacility) {
      fetchServiceabilityData();
    } else {
      setIsLoading(false);
      setServiceability(undefined);
    }
  }, [enrichmentKey]);

  return {
    serviceability,
    isLoading,
    error,
  };
}
